import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { RARITY_TIERS, getRarityTier, type RarityTag } from '../lib/rarity';

type Props = {
  /** Tag actual (null = sin marcar, equivalente a "white" pero más pasivo) */
  value: RarityTag | null;
  /** Callback al cambiar. null = limpiar */
  onChange: (next: RarityTag | null) => void;
  /** Tamaño del dot principal */
  size?: 'sm' | 'md';
};

/**
 * Picker compacto: 1 dot que al tocarlo se expande a la fila de 7 chips.
 * Default invisible (gris muy claro) hasta que el usuario marca una rareza.
 */
export function RarityPicker({ value, onChange, size = 'sm' }: Props) {
  const [open, setOpen] = useState(false);
  const current = getRarityTier(value);
  const dotSize = size === 'md' ? 16 : 12;

  return (
    <div className="relative inline-block">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
        }}
        aria-label="Marcar rareza"
        className="grid place-items-center rounded-full transition hover:scale-110"
        style={{
          width: dotSize + 6,
          height: dotSize + 6,
          background: current ? current.bg : 'rgba(255,255,255,0.05)',
          border: `1.5px solid ${current ? current.border : 'rgba(255,255,255,0.20)'}`,
          boxShadow: current?.glow ?? 'none',
        }}
      />

      <AnimatePresence>
        {open && (
          <>
            {/* Overlay para cerrar al tap afuera */}
            <div
              className="fixed inset-0 z-30"
              onClick={(e) => {
                e.stopPropagation();
                setOpen(false);
              }}
            />
            <motion.div
              initial={{ opacity: 0, y: -6, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -6, scale: 0.9 }}
              transition={{ duration: 0.15 }}
              className="absolute z-40 top-full mt-2 right-0 rounded-2xl bg-ink/95 backdrop-blur-md border border-line/60 p-2 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center gap-1.5">
                {/* Botón "limpiar" si hay tag */}
                {value && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onChange(null);
                      setOpen(false);
                    }}
                    className="grid place-items-center h-7 w-7 rounded-full text-haze text-[10px] font-bold hover:text-foam hover:bg-white/10 transition"
                    aria-label="Limpiar"
                  >
                    ✕
                  </button>
                )}
                {RARITY_TIERS.map((tier) => {
                  const isSelected = tier.id === value;
                  return (
                    <button
                      key={tier.id}
                      onClick={(e) => {
                        e.stopPropagation();
                        onChange(tier.id);
                        setOpen(false);
                      }}
                      title={tier.label + ' — ' + tier.tooltip}
                      className={`grid place-items-center h-7 w-7 rounded-full transition hover:scale-110 ${isSelected ? 'ring-2 ring-foam' : ''}`}
                      style={{
                        background: tier.bg,
                        border: `1.5px solid ${tier.border}`,
                        boxShadow: tier.glow ?? 'none',
                      }}
                    />
                  );
                })}
              </div>
              {/* Label de la actualmente seleccionada */}
              {current && (
                <div className="mt-1.5 px-1 text-[10px] text-haze text-center font-semibold">
                  {current.label} · <span className="opacity-70">{current.tooltip}</span>
                </div>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
